import { inject, injectable } from 'inversify'
import { get, toLower } from 'lodash-es'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  type DependencyToPreload,
  type FilterDependencyProvider,
  type FilterToFormattedTextConvertable,
  type FormattedTextNode,
  FilterOperationEnum,
  type FormattedTextBadgesValueItem,
} from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type ContactListService from '@/services/domain/contactList/contactListService'
import type { FilterToCreate } from '@/services/tables/filters/types'
import ContactList from '@/data/models/domain/ContactList'
import type TranslateService from '@/services/translateService'
import type DeletedEntityService from '@/services/deletedEntityService'

const availableOperations = [FilterOperationEnum.in, FilterOperationEnum.allOf, FilterOperationEnum.exactly]
const entity = 'contactList'

@injectable()
export default class ListFilterMapper implements FilterToFormattedTextConvertable, FilterDependencyProvider {
  constructor(
    @inject(SERVICE_TYPES.ContactListService) protected readonly contactListService: ContactListService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.DeletedEntityService) protected readonly deletedEntityService: DeletedEntityService,
  ) {}

  public toFormattedText(
    filterValue: PaginationUrlFilterScalarValueType,
    { relatedFieldName = 'id' }: FilterToCreate,
  ): FormattedTextNode[] {
    if (filterValue.isEmpty) {
      return [
        {
          type: 'italic',
          value: this.translateService.t('tmFilterContactList.unlisted'),
        },
      ]
    }
    return [
      {
        type: 'badges',
        value: this.getListIds(filterValue, relatedFieldName).map((id) => {
          if (this.deletedEntityService.isDeleted(entity, id)) {
            return {
              name: toLower(this.deletedEntityService.getDeletedEntityLabel(entity)),
              color: 'red',
            } satisfies FormattedTextBadgesValueItem
          }
          return {
            name: this.contactListService.findEntityByIdOrNull(id)?.name ?? id,
            icon: 'tmi-people',
            color: 'blue',
            extraData: {
              entity: {
                type: 'contactList',
                id,
              },
            },
          } satisfies FormattedTextBadgesValueItem
        }),
      },
    ]
  }

  public getFilterDependencies(
    filterValue: PaginationUrlFilterScalarValueType,
    { relatedFieldName = 'id' }: FilterToCreate,
  ): DependencyToPreload[] {
    return [
      {
        model: ContactList,
        ids: this.getListIds(filterValue, relatedFieldName),
      },
    ]
  }

  protected getListIds(filterValue: PaginationUrlFilterScalarValueType, relatedFieldName: string): string[] {
    let listIds: string[] = []
    for (const operation of availableOperations) {
      const ids = get(filterValue, [relatedFieldName, operation]) as string[] | undefined
      if (ids && ids.length > 0) {
        listIds = ids
        break
      }
    }
    return listIds
  }
}
